import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { apiFetchJson } from "../api";
import { UserIcon, XIcon, FileIcon, EditIcon, SendIcon, Spinner } from "./Icons";
import { t, tf } from "../i18n";
import { A, aa, S, sa, onAccent } from "../theme/palette";
import { lockBodyScroll, unlockBodyScroll, useModalTouchGuard } from "../utils/bodyScrollLock";
import { overlayMotionProps, dialogMotionProps } from "../utils/modalPresence";
import { useEscapeLayer } from "../utils/escapeLayers";
import StyledSelect from "./StyledSelect";

// Пределы высоты списка прокси: не выше 240, и не ниже 140 — меньше уже не
// выбрать, лучше докрутить модалку.
const DROPDOWN_MAX_HEIGHT = 240;
const DROPDOWN_MIN_HEIGHT = 140;

// Встроенные прокси — одной строкой «Umbrella», без выбора из пула: какой
// прокси достанется аккаунту, решает сервер — следующий по кругу (umbrella-auto,
// см. resolveRequestedProxyId в backend/src/index.js).
const BUILTIN_PROXY_AUTO_ID = "umbrella-auto";
const collapseBuiltinProxies = (list) => {
  const items = Array.isArray(list) ? list : [];
  const pool = items.filter((proxy) => proxy?.isBuiltin);
  if (!pool.length) return items;
  const anyAlive = pool.some((proxy) => String(proxy?.status || "") !== "failed");
  return [
    { id: BUILTIN_PROXY_AUTO_ID, name: "Umbrella", isBuiltin: true, status: anyAlive ? "active" : "failed", checkResult: null },
    ...items.filter((proxy) => !proxy?.isBuiltin)
  ];
};

const AddAccountModal = ({ isOpen, dialogState = "open", isPhoneView = false, onClose, onSuccess, onBulkSuccess, proxies }) => {
  // Пока окно открыто, страница под ним не прокручивается — иначе на телефоне
  // жест на короткой форме уезжал в фон.
  useEffect(() => {
    if (!isOpen) return undefined;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [isOpen]);
  const overlayRef = useRef(null);
  useModalTouchGuard(overlayRef, isOpen && isPhoneView);
  const [loading, setLoading] = useState(false);
  const [selectedProxy, setSelectedProxy] = useState("");
  const [files, setFiles] = useState([]);
  const [cookieText, setCookieText] = useState("");
  const [accountAddMode, setAccountAddMode] = useState("cookies");
  const [uploadMode, setUploadMode] = useState("file");
  const [registrationForm, setRegistrationForm] = useState({
    email: "",
    password: "",
    contactName: "",
    accountType: "private",
    confirmationUrl: "",
    phoneNationalNumber: "",
    phoneCode: ""
  });
  const [registrationSessionId, setRegistrationSessionId] = useState("");
  const [registrationStage, setRegistrationStage] = useState("details");
  const [registrationNotice, setRegistrationNotice] = useState("");
  const [detectingAccount, setDetectingAccount] = useState(false);
  const [detectedAccount, setDetectedAccount] = useState(null);
  const [detectError, setDetectError] = useState("");
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [availableProxies, setAvailableProxies] = useState([]);
  const [proxyDropdownOpen, setProxyDropdownOpen] = useState(false);
  // Escape: сначала список прокси, если раскрыт, потом само окно.
  useEscapeLayer(isOpen && dialogState !== "closed", () => onClose());
  useEscapeLayer(isOpen && proxyDropdownOpen, () => setProxyDropdownOpen(false));
  // Где стоит список прокси. Внутри карточки он обрезался её краем: колонка
  // формы прокручивается, карточка срезает всё, что за краем, и нижние прокси
  // было не достать, не прокрутив всю модалку. Теперь список рисуется поверх
  // карточки (в слое подложки, position: fixed) по координатам поля: вниз, если
  // снизу хватает экрана, иначе вверх, высотой не больше, чем помещается.
  const [proxyDropdownPlacement, setProxyDropdownPlacement] = useState(null);
  const proxyDropdownRef = useRef(null);
  const proxyDropdownListRef = useRef(null);
  const detectRequestSeqRef = useRef(0);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    setAvailableProxies(collapseBuiltinProxies(proxies));
  }, [proxies]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const loadProxies = async () => {
      try {
        const data = await apiFetchJson("/api/proxies");
        if (!cancelled) {
          setAvailableProxies(collapseBuiltinProxies(data));
        }
      } catch (error) {
        if (!cancelled) {
          setAvailableProxies(collapseBuiltinProxies(proxies));
        }
      }
    };

    loadProxies();

    return () => {
      cancelled = true;
    };
  }, [isOpen, proxies]);

  useEffect(() => {
    if (!selectedProxy) return;
    const stillExists = availableProxies?.some((proxy) => String(proxy.id) === String(selectedProxy));
    if (!stillExists) {
      setSelectedProxy("");
    }
  }, [availableProxies, selectedProxy]);

  useEffect(() => {
    if (isOpen) return;
    setLoading(false);
    setSelectedProxy("");
    setFiles([]);
    setCookieText("");
    setAccountAddMode("cookies");
    setUploadMode("file");
    setRegistrationForm({
      email: "",
      password: "",
      contactName: "",
      accountType: "private",
      confirmationUrl: "",
      phoneNationalNumber: "",
      phoneCode: ""
    });
    setRegistrationSessionId("");
    setRegistrationStage("details");
    setRegistrationNotice("");
    setProxyDropdownOpen(false);
    setDetectingAccount(false);
    setDetectedAccount(null);
    setDetectError("");
    setUploadProgress({ current: 0, total: 0 });
    detectRequestSeqRef.current += 1;
    submitInFlightRef.current = false;
  }, [isOpen]);

  const extractJsonArraySegments = (rawValue) => {
    const text = String(rawValue || "");
    if (!text.includes("[") || !text.includes("]")) return [];
    const segments = [];
    let start = -1;
    let depth = 0;
    let inString = false;
    let escaped = false;

    for (let index = 0; index < text.length; index += 1) {
      const char = text[index];
      if (inString) {
        if (escaped) {
          escaped = false;
          continue;
        }
        if (char === "\\") {
          escaped = true;
          continue;
        }
        if (char === "\"") {
          inString = false;
        }
        continue;
      }
      if (char === "\"") {
        inString = true;
        continue;
      }
      if (char === "[") {
        if (depth === 0) {
          start = index;
        }
        depth += 1;
        continue;
      }
      if (char === "]" && depth > 0) {
        depth -= 1;
        if (depth === 0 && start >= 0) {
          segments.push(text.slice(start, index + 1));
          start = -1;
        }
      }
    }
    return segments;
  };

  const normalizeCookiePayload = (rawValue) => {
    const text = String(rawValue || "").replace(/\u0000/g, "").trim();
    if (!text) return "";

    // 1) Try to extract a JSON array (cookie export format)
    const jsonSegments = extractJsonArraySegments(text);
    if (jsonSegments.length) {
      // Pick the largest JSON array — likely the cookies
      let best = jsonSegments[0];
      for (const seg of jsonSegments) {
        if (seg.length > best.length) best = seg;
      }
      return best.trim();
    }

    // 2) Fallback: bracket slice (first '[' to last ']')
    const firstBracket = text.indexOf("[");
    const lastBracket = text.lastIndexOf("]");
    if (firstBracket >= 0 && lastBracket > firstBracket) {
      const slice = text.slice(firstBracket, lastBracket + 1).trim();
      // Only use if it looks like valid JSON array start
      if (/^\[\s*\{/.test(slice)) return slice;
    }

    // 3) Extract cookie-like lines from garbage text
    const lines = text.split(/\r?\n/);
    const cookieLines = [];
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      // Netscape format: domain TAB flag TAB path TAB secure TAB expires TAB name TAB value
      if (trimmed.split("\t").length >= 7) { cookieLines.push(trimmed); continue; }
      // Set-Cookie header
      if (/^set-cookie:/i.test(trimmed)) { cookieLines.push(trimmed); continue; }
      // Cookie header line
      if (/^cookie:/i.test(trimmed)) { cookieLines.push(trimmed); continue; }
      // key=value pairs with semicolons (cookie header style)
      if (trimmed.includes("=") && trimmed.includes(";") && /;\s*[^=;\s]+=/.test(trimmed)) { cookieLines.push(trimmed); continue; }
      // Simple key=value (potential cookie pair)
      if (/^[a-zA-Z_][a-zA-Z0-9_.~-]*=/.test(trimmed) && !trimmed.includes(" ")) { cookieLines.push(trimmed); continue; }
    }
    if (cookieLines.length > 0) return cookieLines.join("\n");

    return text;
  };

  const splitCookieTextEntries = (rawValue) => {
    const raw = String(rawValue || "").replace(/\u0000/g, "");
    const arrays = extractJsonArraySegments(raw)
      .map((segment) => normalizeCookiePayload(segment))
      .filter(Boolean);
    if (arrays.length) {
      return Array.from(new Set(arrays));
    }

    const chunks = raw
      .split(/(?:\r?\n)\s*---+\s*(?:\r?\n)|\r?\n{2,}/)
      .map((chunk) => normalizeCookiePayload(chunk))
      .filter(Boolean);
    if (chunks.length) {
      return chunks;
    }

    const single = normalizeCookiePayload(raw);
    return single ? [single] : [];
  };

  const runCookieAutodetect = async (rawCookieText) => {
    const text = normalizeCookiePayload(rawCookieText);
    const seq = detectRequestSeqRef.current + 1;
    detectRequestSeqRef.current = seq;

    if (!text) {
      setDetectingAccount(false);
      setDetectedAccount(null);
      setDetectError("");
      return;
    }

    setDetectingAccount(true);
    setDetectError("");
    try {
      const response = await apiFetchJson("/api/accounts/detect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Passing the selected proxy lets the backend resolve the real account
        // name (the web token no longer carries it) instead of showing an ID.
        body: JSON.stringify({ cookieText: text, proxyId: selectedProxy || "" }),
        timeoutMs: 90000,
        allowBaseFallback: true
      });
      if (seq !== detectRequestSeqRef.current) return;
      setDetectedAccount(response?.detected || null);
      setDetectError("");
    } catch (error) {
      if (seq !== detectRequestSeqRef.current) return;
      setDetectedAccount(null);
      setDetectError(error?.message || t("Не удалось определить аккаунт по cookies."));
    } finally {
      if (seq === detectRequestSeqRef.current) {
        setDetectingAccount(false);
      }
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    let timerId;
    let cancelled = false;

    const detectFromFile = async () => {
      if (files.length !== 1) {
        runCookieAutodetect("");
        return;
      }
      setDetectingAccount(true);
      setDetectError("");
      try {
        const text = await files[0].text();
        if (cancelled) return;
        await runCookieAutodetect(text);
      } catch (error) {
        if (cancelled) return;
        setDetectingAccount(false);
        setDetectedAccount(null);
        setDetectError(t("Не удалось прочитать файл cookies."));
      }
    };

    if (uploadMode === "file") {
      detectFromFile();
    } else {
      const textEntries = splitCookieTextEntries(cookieText);
      if (textEntries.length !== 1) {
        runCookieAutodetect("");
        return () => {
          cancelled = true;
        };
      }
      timerId = window.setTimeout(() => {
        runCookieAutodetect(textEntries[0]);
      }, 450);
    }

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
    // selectedProxy is a dep so that choosing a proxy after loading the cookies
    // re-runs detection and resolves the real name via the backend profile fetch.
  }, [isOpen, uploadMode, files, cookieText, selectedProxy]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []).filter(Boolean);
    setFiles(selectedFiles);
  };

  const updateRegistrationField = (field, value) => {
    setRegistrationForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    if (loading || submitInFlightRef.current) return;
    if (!selectedProxy) {
      alert(t("Выберите прокси для регистрации"));
      return;
    }

    const email = String(registrationForm.email || "").trim();
    const password = String(registrationForm.password || "");
    const contactName = String(registrationForm.contactName || "").trim();

    submitInFlightRef.current = true;
    setLoading(true);
    try {
      if (!registrationSessionId) {
        if (!email || !password || !contactName) {
          alert(t("Заполните email, пароль и имя профиля"));
          return;
        }
        const result = await apiFetchJson("/api/accounts/register/start", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            proxyId: selectedProxy,
            email,
            password,
            contactName,
            accountType: registrationForm.accountType
          }),
          timeoutMs: 180000,
          allowBaseFallback: true
        });
        setRegistrationSessionId(result.sessionId || "");
        setRegistrationStage("confirm");
        setRegistrationNotice(result.message || t("Регистрация отправлена. Вставьте ссылку из письма."));
        return;
      }

      if (registrationStage === "confirm") {
        const confirmationUrl = String(registrationForm.confirmationUrl || "").trim();
        if (!confirmationUrl) {
          alert(t("Вставьте ссылку подтверждения из письма"));
          return;
        }
        const result = await apiFetchJson("/api/accounts/register/confirm-link", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: registrationSessionId,
            confirmationUrl,
            phoneNationalNumber: registrationForm.phoneNationalNumber,
            countryCallingCode: 49
          }),
          timeoutMs: 180000,
          allowBaseFallback: true
        });
        const requestedPhone = String(registrationForm.phoneNationalNumber || "").replace(/[^\d]/g, "");
        if (requestedPhone && result?.phoneVerificationStarted) {
          setRegistrationStage("phoneCode");
          setRegistrationNotice(result.message || t("SMS-код отправлен. Введите код из сообщения."));
        } else {
          setRegistrationStage("finalize");
          setRegistrationNotice(result.message || t("Email-ссылка открыта. Теперь можно выполнить вход и добавить аккаунт."));
        }
        return;
      }

      if (registrationStage === "phoneCode") {
        const phoneCode = String(registrationForm.phoneCode || "").replace(/[^\d]/g, "");
        if (phoneCode.length < 4) {
          alert(t("Введите корректный SMS-код телефона"));
          return;
        }
        const result = await apiFetchJson("/api/accounts/register/confirm-phone", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: registrationSessionId,
            code: phoneCode
          }),
          timeoutMs: 120000,
          allowBaseFallback: true
        });
        setRegistrationStage("finalize");
        setRegistrationNotice(result.message || t("Телефон подтвержден. Теперь можно выполнить вход и добавить аккаунт."));
        return;
      }

      const result = await apiFetchJson("/api/accounts/register/finalize", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: registrationSessionId }),
        timeoutMs: 240000,
        allowBaseFallback: true
      });
      if (result?.success && result?.account) {
        onSuccess(result.account, result.metrics || null);
        alert(result.message || t("Аккаунт зарегистрирован и добавлен"));
        onClose();
      } else {
        alert(result?.error || t("Не удалось завершить регистрацию"));
      }
    } catch (error) {
      alert(error?.message || t("Не удалось выполнить регистрацию"));
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (accountAddMode === "registration") {
      await handleRegistrationSubmit(e);
      return;
    }
    if (loading || submitInFlightRef.current) return;
    const uploadItems = [];
    if (uploadMode === "file") {
      if (!files.length) {
        alert(t("Пожалуйста, выберите хотя бы один файл"));
        return;
      }
      files.forEach((sourceFile, index) => {
        uploadItems.push({
          type: "file",
          sourceName: sourceFile?.name || `cookies-${index + 1}.txt`,
          sourceFile
        });
      });
    } else {
      const textEntries = splitCookieTextEntries(cookieText);
      if (!textEntries.length) {
        alert(t("Пожалуйста, вставьте куки"));
        return;
      }
      textEntries.forEach((entry, index) => {
        uploadItems.push({
          type: "text",
          sourceName: `cookies-${index + 1}.txt`,
          payloadText: entry
        });
      });
    }

    if (!selectedProxy) {
      alert(t("Выберите прокси для аккаунта"));
      return;
    }

    const buildFormDataForItem = (item) => {
      const formData = new FormData();
      if (item.type === "file" && item.sourceFile) {
        formData.append("cookieFile", item.sourceFile, item.sourceName);
      } else {
        const blob = new Blob([normalizeCookiePayload(item.payloadText)], { type: "text/plain" });
        formData.append("cookieFile", blob, item.sourceName);
      }
      formData.append("proxyId", selectedProxy);
      return formData;
    };

    const uploadSingleItem = async (item) => {
      const requestId = `acc-upload-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      return apiFetchJson("/api/accounts/upload", {
        method: "POST",
        body: buildFormDataForItem(item),
        timeoutMs: 240000,
        allowBaseFallback: true,
        requestId
      });
    };

    submitInFlightRef.current = true;
    setLoading(true);
    setUploadProgress({ current: 0, total: uploadItems.length });
    const uploaded = [];
    const failed = [];

    try {
      // Bulk upload runs as a worker pool with bounded concurrency:
      // each worker pulls the next pending item from a shared cursor
      // and POSTs it independently. Upload of N accounts now takes
      // roughly ceil(N / CONCURRENCY) × per-upload time instead of
      // N × per-upload time. Проверка куки на сервере — обычные HTTP-запросы
      // без браузера и без своей очереди, так что 10 аккаунтов за проход
      // сервер держит; больше — уже заметная пачка с одного прокси.
      const CONCURRENCY = 10;
      let nextIndex = 0;
      let completedCount = 0;

      const processItem = async (item) => {
        try {
          const result = await uploadSingleItem(item);
          if (result?.success && result?.account) {
            uploaded.push({
              account: result.account,
              metrics: result.metrics || null,
              sourceName: item.sourceName
            });
          } else {
            failed.push({
              sourceName: item.sourceName,
              error: result?.error || t("Неизвестная ошибка")
            });
          }
        } catch (error) {
          const details = [];
          if (error?.url) details.push(error.url);
          if (typeof window !== "undefined" && window.location?.origin) details.push(window.location.origin);
          const suffix = details.length ? ` (${details.join(", ")})` : "";
          failed.push({
            sourceName: item.sourceName,
            error: (error?.message || "Failed to fetch") + suffix
          });
        } finally {
          completedCount += 1;
          setUploadProgress({ current: completedCount, total: uploadItems.length });
        }
      };

      const workerCount = Math.max(1, Math.min(CONCURRENCY, uploadItems.length));
      const workers = Array.from({ length: workerCount }, async () => {
        while (true) {
          const myIndex = nextIndex;
          nextIndex += 1;
          if (myIndex >= uploadItems.length) return;
          await processItem(uploadItems[myIndex]);
        }
      });
      await Promise.all(workers);

      if (uploaded.length) {
        if (uploaded.length > 1 && typeof onBulkSuccess === "function") {
          onBulkSuccess(uploaded);
        } else {
          uploaded.forEach((entry) => onSuccess(entry.account, entry.metrics || null));
        }
      }

      if (uploaded.length && failed.length === 0) {
        alert(tf`Добавлено аккаунтов: ${uploaded.length}`);
        onClose();
      } else if (uploaded.length && failed.length > 0) {
        const preview = failed.slice(0, 2).map((item) => `• ${item.sourceName}: ${item.error}`).join("\n");
        alert(tf`Добавлено ${uploaded.length} из ${uploadItems.length}.\nОшибок: ${failed.length}${preview ? `\n${preview}` : ""}`);
        onClose();
      } else {
        const preview = failed.slice(0, 3).map((item) => `• ${item.sourceName}: ${item.error}`).join("\n");
        alert(tf`Не удалось добавить аккаунты (${failed.length}/${uploadItems.length}).${preview ? `\n${preview}` : ""}`);
      }
    } finally {
      submitInFlightRef.current = false;
      setLoading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  // Положение считается до отрисовки (layout effect), чтобы список не мелькал не
  // там, и пересчитывается при прокрутке формы и смене размера окна: он стоит
  // поверх карточки, а не в ней, и сам за полем не едет. Если поле уехало за
  // край формы, список закрывается — висеть отдельно от поля ему незачем.
  useLayoutEffect(() => {
    if (!isOpen || !proxyDropdownOpen || typeof window === "undefined") return undefined;
    const place = () => {
      const trigger = proxyDropdownRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      let visibleTop = 0;
      let visibleBottom = window.innerHeight;
      for (let node = trigger.parentElement; node && node !== document.body; node = node.parentElement) {
        if (window.getComputedStyle(node).overflowY === "visible") continue;
        const box = node.getBoundingClientRect();
        visibleTop = Math.max(visibleTop, box.top);
        visibleBottom = Math.min(visibleBottom, box.bottom);
      }
      if (rect.bottom <= visibleTop || rect.top >= visibleBottom) {
        setProxyDropdownOpen(false);
        return;
      }
      const gap = 8;
      const edge = 12; // отступ от края экрана
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom - gap - edge;
      const spaceAbove = rect.top - gap - edge;
      const openUp = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
      const space = openUp ? spaceAbove : spaceBelow;
      setProxyDropdownPlacement({
        left: rect.left,
        width: rect.width,
        up: openUp,
        top: openUp ? null : rect.bottom + gap,
        bottom: openUp ? viewportHeight - rect.top + gap : null,
        maxHeight: Math.max(DROPDOWN_MIN_HEIGHT, Math.min(DROPDOWN_MAX_HEIGHT, Math.floor(space)))
      });
    };
    place();
    let frame = 0;
    const schedule = (event) => {
      // Прокрутка самого списка его положения не меняет.
      // У resize источник — window, а не узел, и contains на нём падает.
      const source = event?.target;
      if (source instanceof Node && proxyDropdownListRef.current?.contains(source)) return;
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        place();
      });
    };
    window.addEventListener("resize", schedule);
    document.addEventListener("scroll", schedule, true);
    return () => {
      window.removeEventListener("resize", schedule);
      document.removeEventListener("scroll", schedule, true);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [isOpen, proxyDropdownOpen]);

  useEffect(() => {
    if (!isOpen || !proxyDropdownOpen) return undefined;
    // Список живёт вне поля (в слое подложки), поэтому нажатие в нём — тоже
    // «внутри»: иначе он закрывался на mousedown, и выбор прокси не срабатывал.
    const handleOutsideClick = (event) => {
      if (proxyDropdownRef.current?.contains(event.target)) return;
      if (proxyDropdownListRef.current?.contains(event.target)) return;
      setProxyDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, proxyDropdownOpen]);

  const selectedProxyMeta = availableProxies.find((proxy) => String(proxy.id) === String(selectedProxy)) || null;

  if (!isOpen) return null;

  const labelStyle = {
    display: "block",
    marginBottom: "10px",
    fontWeight: "700",
    color: A[100],
    fontSize: "13px",
    letterSpacing: "0.02em"
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    border: "1px solid rgba(141,147,163,0.28)",
    borderRadius: "18px",
    backgroundColor: "rgba(8,9,12,0.55)",
    color: "#e2e4e9",
    fontSize: "14px",
    transition: "all 0.2s ease",
    outline: "none",
    boxShadow: "inset 0 0 0 1px rgba(23,25,33,0.68)"
  };

  const sectionCardStyle = {
    borderRadius: "16px",
    border: "1px solid rgba(141,147,163,0.2)",
    background: S.surface,
    padding: "14px 14px 12px"
  };

  const accentButtonStyle = {
    borderRadius: "16px",
    border: `1px solid ${aa(400, 0.4)}`,
    background: `linear-gradient(135deg, ${aa(700, 0.92)}, ${aa(600, 0.86)})`,
    color: onAccent(A[50]),
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: "700"
  };
  const applyHoverStyles = (event, styles) => {
    if (!event?.currentTarget || !styles) return;
    Object.assign(event.currentTarget.style, styles);
  };
  const actionHoverEnter = {
    background: `linear-gradient(135deg, ${aa(700, 0.38)}, ${aa(600, 0.26)})`,
    borderColor: aa(300, 0.56),
    boxShadow: `0 14px 28px ${aa(900, 0.24)}`,
    transform: "translateY(-2px)"
  };
  const accentHoverEnter = {
    ...actionHoverEnter,
    filter: "brightness(1.06)"
  };
  const accentHoverLeave = {
    background: accentButtonStyle.background,
    borderColor: aa(400, 0.4),
    boxShadow: "none",
    transform: "translateY(0)",
    filter: "none"
  };

  const textCookieEntries = uploadMode === "text" ? splitCookieTextEntries(cookieText) : [];
  const batchEntriesCount = uploadMode === "file" ? files.length : textCookieEntries.length;
  const isBatchMode = batchEntriesCount > 1;

  const autodetectInlineNotice = (
    <div style={{
      marginTop: "10px",
      padding: "10px 12px",
      borderRadius: "16px",
      border: detectingAccount
        ? `1px solid ${aa(300, 0.35)}`
        : detectError
          ? "1px solid rgba(248,113,113,0.35)"
          : isBatchMode
            ? `1px solid ${aa(300, 0.34)}`
          : detectedAccount?.validCandidate
            ? "1px solid rgba(110,231,183,0.36)"
            : "1px solid rgba(141,147,163,0.24)",
      background: detectingAccount
        ? `linear-gradient(135deg, ${aa(800, 0.2)}, ${aa(600, 0.18)})`
        : detectError
          ? "linear-gradient(135deg, rgba(127,29,29,0.3), rgba(69,10,10,0.28))"
          : isBatchMode
            ? `linear-gradient(135deg, ${aa(800, 0.22)}, ${aa(700, 0.18)})`
          : detectedAccount?.validCandidate
            ? "linear-gradient(135deg, rgba(5,150,105,0.2), rgba(6,95,70,0.2))"
            : "rgba(8,9,12,0.45)",
      color: detectError ? "#fecaca" : "#c6c9d1",
      fontSize: "12px",
      lineHeight: 1.45
    }}>
      {detectingAccount ? (
        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", color: A[100], fontWeight: 600 }}>
          <Spinner size={14} />
          {t("Определяем аккаунт по cookies...")}
        </div>
      ) : detectError ? (
        <span>{detectError}</span>
      ) : isBatchMode ? (
        <span>
          {t("Режим массового импорта: найдено")} {batchEntriesCount} {uploadMode === "file" ? t("файл(ов)") : t("блок(ов) cookies")}.
        </span>
      ) : detectedAccount ? (
        <span>
          {detectedAccount.validCandidate ? t("Аккаунт обнаружен") : t("Cookies распознаны частично")}
          {detectedAccount.accountLabel ? `: ${detectedAccount.accountLabel}` : ""}
          {detectedAccount.profileEmail ? ` · ${detectedAccount.profileEmail}` : ""}
        </span>
      ) : (
        <span>{t("После загрузки файла или вставки текста данные аккаунта определятся автоматически.")}</span>
      )}
    </div>
  );

  return (
    <div ref={overlayRef} className="modal-overlay add-account-modal-root" {...overlayMotionProps(dialogState)} style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 15% 10%, ${aa(700, 0.26)}, rgba(8,9,12,0.92) 46%, rgba(8,9,12,0.98) 100%)`,
      backdropFilter: "blur(10px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: isPhoneView ? "12px" : "20px"
    }}>
      <div className="modal-card add-account-modal-card" data-modal-scroll="1" {...dialogMotionProps(dialogState)} style={{
        position: "relative",
        background: S.surface,
        borderRadius: "20px",
        padding: 0,
        width: isPhoneView ? "100%" : "700px",
        maxWidth: "100%",
        maxHeight: "90vh",
        // На телефоне прокручивается вся карточка, шапка вместе с формой, а
        // внизу липнет только кнопка — как в карточке объявления.
        overflowY: isPhoneView ? "auto" : "hidden",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
        border: "1px solid rgba(255,255,255,0.09)",
        boxShadow: "0 34px 72px rgba(0,0,0,0.54)",
        color: "#e2e4e9",
        display: isPhoneView ? "flex" : "grid",
        flexDirection: isPhoneView ? "column" : undefined,
        gridTemplateColumns: isPhoneView ? undefined : "236px minmax(0, 1fr)"
      }}>
        {/* ── Left: method navigation ── */}
        <div style={{
          background: "rgba(255,255,255,0.015)",
          borderRight: isPhoneView ? "none" : "1px solid rgba(255,255,255,0.06)",
          borderBottom: isPhoneView ? "1px solid rgba(255,255,255,0.06)" : "none",
          padding: isPhoneView ? "18px 16px" : "24px 16px",
          display: "flex",
          flexDirection: "column"
        }}>
          <div style={{ padding: "0 8px 18px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: "17px", fontWeight: 700, letterSpacing: "-0.02em", color: "#f2f3f6" }}>{t("Добавить аккаунт")}</h2>
              <p style={{ margin: "5px 0 0", fontSize: "12px", color: "#8d93a3", lineHeight: 1.4 }}>{t("Выберите способ добавления")}</p>
            </div>
            {/* Крестик — тот же, что в карточке объявления. На столе он стоит
                над формой справа; здесь, в шапке, — только на телефоне. */}
            {isPhoneView && (
              <button
                type="button"
                className="kl-plain-button"
                onClick={onClose}
                aria-label={t("Закрыть")}
                style={{
                  width: "44px", height: "44px", flexShrink: 0, borderRadius: "12px",
                  background: `${sa(0.68)}`, border: "1px solid rgba(141,147,163,0.3)",
                  color: "#c6c9d1", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer"
                }}
              >
                <XIcon size={16} />
              </button>
            )}
          </div>
          <div style={{ display: isPhoneView ? "grid" : "flex", gridTemplateColumns: isPhoneView ? "1fr 1fr" : undefined, flexDirection: isPhoneView ? undefined : "column", gap: "4px" }}>
            {[
              { key: "file", icon: <UserIcon size={18} />, label: t("Загрузить файл"), desc: t("Файл с cookies (.json / .txt)"), active: accountAddMode === "cookies" && uploadMode === "file", onClick: () => { setAccountAddMode("cookies"); setUploadMode("file"); } },
              { key: "text", icon: <EditIcon size={18} />, label: t("Вставить текст"), desc: t("Содержимое cookies вручную"), active: accountAddMode === "cookies" && uploadMode === "text", onClick: () => { setAccountAddMode("cookies"); setUploadMode("text"); } }
            ].map((m) => (
              <button
                key={m.key}
                type="button"
                className="kl-plain-button"
                onClick={m.onClick}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "11px",
                  padding: "12px",
                  borderRadius: "12px",
                  cursor: "pointer",
                  textAlign: "left",
                  background: m.active ? aa(500, 0.14) : "transparent",
                  border: m.active ? `1px solid ${aa(500, 0.3)}` : "1px solid transparent",
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={(e) => { if (!m.active) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
                onMouseLeave={(e) => { if (!m.active) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ color: m.active ? A[300] : "#8d93a3", marginTop: "1px", flexShrink: 0 }}>{m.icon}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: "13.5px", fontWeight: 600, color: m.active ? "#f2f3f6" : "#c6c9d1" }}>{m.label}</span>
                  {!isPhoneView && <span style={{ display: "block", fontSize: "11.5px", color: "#707684", marginTop: "2px", lineHeight: 1.35 }}>{m.desc}</span>}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Right: form column ── */}
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0, maxHeight: isPhoneView ? "none" : "90vh", overflowY: isPhoneView ? "visible" : "auto" }}>
          {!isPhoneView && (
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "18px 22px 0" }}>
              <button
                type="button"
                className="kl-plain-button"
                onClick={onClose}
                aria-label={t("Закрыть")}
                style={{
                  width: "44px", height: "44px", borderRadius: "12px",
                  background: `${sa(0.68)}`, border: "1px solid rgba(141,147,163,0.3)",
                  color: "#c6c9d1", display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer"
                }}
              >
                <XIcon size={16} />
              </button>
            </div>
          )}
          <div style={{ padding: isPhoneView ? "4px 16px 0" : "4px 24px 24px" }}>
        <form onSubmit={handleSubmit}>
          {accountAddMode === "registration" ? (
            <div style={{ ...sectionCardStyle, marginBottom: "12px", display: "grid", gap: "12px" }}>
              <div style={{
                display: "grid",
                gridTemplateColumns: isPhoneView ? "1fr" : "1fr 1fr",
                gap: "12px"
              }}>
                <div>
                  <label style={labelStyle}>Email:</label>
                  <input
                    type="email"
                    value={registrationForm.email}
                    onChange={(e) => updateRegistrationField("email", e.target.value)}
                    placeholder="name@example.com"
                    disabled={Boolean(registrationSessionId)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t("Пароль:")}</label>
                  <input
                    type="password"
                    value={registrationForm.password}
                    onChange={(e) => updateRegistrationField("password", e.target.value)}
                    placeholder={t("Минимум 8 символов")}
                    disabled={Boolean(registrationSessionId)}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{
                display: "grid",
                gridTemplateColumns: isPhoneView ? "1fr" : "1fr 1fr",
                gap: "12px"
              }}>
                <div>
                  <label style={labelStyle}>{t("Имя профиля:")}</label>
                  <input
                    value={registrationForm.contactName}
                    onChange={(e) => updateRegistrationField("contactName", e.target.value)}
                    placeholder="Max Mustermann"
                    disabled={Boolean(registrationSessionId)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t("Тип аккаунта:")}</label>
                  <StyledSelect
                    value={registrationForm.accountType}
                    onChange={(next) => updateRegistrationField("accountType", next)}
                    disabled={Boolean(registrationSessionId)}
                    options={[
                      { value: "private", label: "Privat" },
                      { value: "gewerblich", label: "Gewerblich" }
                    ]}
                    ariaLabel={t("Тип аккаунта")}
                  />
                </div>
              </div>

              {registrationSessionId && (
                <>
                  <div>
                    <label style={labelStyle}>{t("Ссылка из письма:")}</label>
                    <textarea
                      value={registrationForm.confirmationUrl}
                      onChange={(e) => updateRegistrationField("confirmationUrl", e.target.value)}
                      placeholder="https://www.kleinanzeigen.de/m-benutzer-verifizieren.html?uuid=..."
                      disabled={registrationStage !== "confirm"}
                      style={{
                        ...inputStyle,
                        minHeight: "74px",
                        resize: "vertical"
                      }}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>{t("Телефон DE без +49 (опционально):")}</label>
                    <input
                      value={registrationForm.phoneNationalNumber}
                      onChange={(e) => updateRegistrationField("phoneNationalNumber", e.target.value)}
                      placeholder="17612345678"
                      disabled={registrationStage !== "confirm"}
                      style={inputStyle}
                    />
                  </div>
                  {registrationStage === "phoneCode" && (
                    <div>
                      <label style={labelStyle}>{t("SMS-код телефона:")}</label>
                      <input
                        value={registrationForm.phoneCode}
                        onChange={(e) => updateRegistrationField("phoneCode", e.target.value.replace(/[^\d]/g, "").slice(0, 8))}
                        placeholder="123456"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        style={inputStyle}
                      />
                    </div>
                  )}
                </>
              )}

              <div style={{
                padding: "10px 12px",
                borderRadius: "16px",
                border: `1px solid ${aa(300, 0.28)}`,
                background: aa(500, 0.08),
                color: "#c6c9d1",
                fontSize: "12px",
                lineHeight: 1.5
              }}>
                {registrationNotice || (
                  registrationSessionId
                    ? registrationStage === "phoneCode"
                      ? t("Введите SMS-код, который пришел на указанный телефон.")
                      : t("Вставьте ссылку подтверждения из письма и продолжите.")
                    : t("Сначала отправляется регистрация, затем вставляется email-ссылка. Если указан телефон, следующим шагом будет ввод SMS-кода.")
                )}
              </div>
            </div>
          ) : uploadMode === "file" ? (
            <div style={{ ...sectionCardStyle, marginBottom: "12px" }}>
              <label style={labelStyle}>{t("Файл с куки:")}</label>
              <div
                style={{
                  padding: "28px 20px",
                  border: "2px dashed rgba(141,147,163,0.35)",
                  borderRadius: "16px",
                  backgroundColor: "rgba(8,9,12,0.5)",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  position: "relative"
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.currentTarget.style.borderColor = aa(400, 0.55);
                  e.currentTarget.style.background = aa(700, 0.18);
                }}
                onDragLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(141,147,163,0.35)";
                  e.currentTarget.style.background = "rgba(8,9,12,0.5)";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const droppedFiles = Array.from(e.dataTransfer.files || []).filter(Boolean);
                  if (droppedFiles.length) setFiles(droppedFiles);
                  e.currentTarget.style.borderColor = "rgba(141,147,163,0.35)";
                  e.currentTarget.style.background = "rgba(8,9,12,0.5)";
                }}
                onClick={() => document.getElementById("cookie-file-input").click()}
              >
                <input
                  id="cookie-file-input"
                  type="file"
                  accept=".txt,.json,.cookies"
                  multiple
                  onChange={handleFileChange}
                  style={{
                    display: "none"
                  }}
                />
                <div style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "16px",
                  background: `linear-gradient(135deg, ${aa(700, 0.32)}, ${aa(600, 0.24)})`,
                  border: `1px solid ${aa(400, 0.4)}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px"
                }}>
                  <FileIcon size={28} color={A[200]} />
                </div>
                <p style={{ margin: 0, color: "#8d93a3", fontSize: "14px" }}>
                  {t("Перетащите файл сюда или")} <span style={{ color: A[200], fontWeight: "700" }}>{t("выберите")}</span>
                </p>
                <p style={{ margin: "8px 0 0", color: "#707684", fontSize: "12px" }}>
                  {t("Поддерживаются: .txt, .json, .cookies")}
                </p>
              </div>
              {files.length > 0 && (
                <div style={{
                  marginTop: "12px",
                  padding: "12px 16px",
                  background: `linear-gradient(135deg, ${aa(800, 0.24)}, ${aa(600, 0.2)})`,
                  borderRadius: "16px",
                  border: `1px solid ${aa(300, 0.35)}`,
                  display: "grid",
                  gap: "8px"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", color: A[100], fontWeight: 700, fontSize: "13px" }}>
                    <FileIcon size={18} color={A[200]} />
                    {t("Выбрано файлов:")} {files.length}
                  </div>
                  {files.slice(0, 3).map((item, index) => (
                    <div key={`${item.name}-${index}`} style={{ color: "#c6c9d1", fontSize: "12px" }}>
                      • {item.name} ({(Number(item.size || 0) / 1024).toFixed(2)} KB)
                    </div>
                  ))}
                  {files.length > 3 && (
                    <div style={{ color: "#8d93a3", fontSize: "12px" }}>
                      {t("и еще")} {files.length - 3} {t("файл(ов)")}
                    </div>
                  )}
                </div>
              )}
              {autodetectInlineNotice}
            </div>
          ) : (
            <div style={{ ...sectionCardStyle, marginBottom: "12px" }}>
              <label style={labelStyle}>{t("Текст куки:")}</label>
              <textarea
                value={cookieText}
                onChange={(e) => setCookieText(e.target.value)}
                placeholder={t("Вставьте содержимое файла куки здесь...")}
                style={{
                  width: "100%",
                  height: "200px",
                  padding: "16px",
                  border: "1px solid rgba(141,147,163,0.28)",
                  borderRadius: "18px",
                  backgroundColor: "rgba(8,9,12,0.55)",
                  color: "#e2e4e9",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "13px",
                  resize: "vertical",
                  outline: "none",
                  transition: "all 0.2s ease",
                  boxShadow: "inset 0 0 0 1px rgba(23,25,33,0.68)"
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = aa(400, 0.55);
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${aa(700, 0.2)}`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "rgba(141,147,163,0.28)";
                  e.currentTarget.style.boxShadow = "inset 0 0 0 1px rgba(23,25,33,0.68)";
                }}
              />
              {autodetectInlineNotice}
            </div>
          )}

          <div style={{ ...sectionCardStyle, marginBottom: "14px" }}>
            <label style={labelStyle}>{t("Выберите прокси:")}</label>
            {/* Вид — общий для выпадающих полей панели (.ui-select*, как в
                StyledSelect); своё здесь только положение списка: он стоит в
                слое подложки и едет за полем при прокрутке формы. */}
            <div ref={proxyDropdownRef} className={`ui-select${proxyDropdownOpen ? " is-open" : ""}`}>
              <button
                type="button"
                className="ui-select-trigger"
                aria-haspopup="listbox"
                aria-expanded={proxyDropdownOpen}
                onClick={() => setProxyDropdownOpen((prev) => !prev)}
              >
                <span className={`ui-select-value${selectedProxyMeta ? "" : " is-placeholder"}`}>
                  {selectedProxyMeta ? (
                    <>
                      {selectedProxyMeta.name || "Proxy"}
                      {!selectedProxyMeta.isBuiltin && (
                        <span className="ui-select-value-sub">{` · ${selectedProxyMeta.host}:${selectedProxyMeta.port}`}</span>
                      )}
                    </>
                  ) : t("Выберите прокси")}
                </span>
                <svg className="ui-select-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {proxyDropdownOpen && proxyDropdownPlacement && overlayRef.current && createPortal(
                // Пометка для защиты жестов окна: без неё на телефоне свайп по
                // списку гасился, и длинный список прокси не листался.
                // Слой подложки, а не document.body: там действуют светлая тема
                // окна (.add-account-modal-root) и его защита жестов.
                <div
                  ref={proxyDropdownListRef}
                  role="listbox"
                  data-modal-scroll="1"
                  className={`ui-select-menu is-floating${proxyDropdownPlacement.up ? " is-up" : ""}`}
                  style={{
                    top: proxyDropdownPlacement.up ? "auto" : `${proxyDropdownPlacement.top}px`,
                    bottom: proxyDropdownPlacement.up ? `${proxyDropdownPlacement.bottom}px` : "auto",
                    left: `${proxyDropdownPlacement.left}px`,
                    width: `${proxyDropdownPlacement.width}px`,
                    maxHeight: `${proxyDropdownPlacement.maxHeight}px`
                  }}
                >
                  {availableProxies.length === 0 ? (
                    <div className="ui-select-empty">{t("Нет доступных прокси")}</div>
                  ) : availableProxies.map((proxy) => {
                    const isSelected = String(proxy.id) === String(selectedProxy);
                    return (
                      <button
                        key={proxy.id}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={`ui-select-option ui-select-option--hover${isSelected ? " is-selected" : ""}`}
                        onClick={() => {
                          setSelectedProxy(String(proxy.id));
                          setProxyDropdownOpen(false);
                        }}
                      >
                        <span className="ui-select-option-text">
                          <span className="ui-select-option-label">
                            {proxy.name || "Proxy"}
                            {proxy.isBuiltin && <span className="ui-select-tag">{t("Встроенный")}</span>}
                          </span>
                          <span className="ui-select-option-sub">
                            {proxy.isBuiltin
                              ? (proxy.checkResult?.location?.country
                                  ? `${proxy.checkResult.location.country}${proxy.checkResult.location.city ? " · " + proxy.checkResult.location.city : ""}`
                                  : t("Готов к использованию"))
                              : `${proxy.host}:${proxy.port}`}
                          </span>
                        </span>
                        {isSelected && (
                          <span className="ui-select-check">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>,
                overlayRef.current
              )}
            </div>
          </div>

          {/* Одна кнопка; «Отмена» не нужна — закрывает крестик. На телефоне
              кнопка липнет к низу карточки, как подвал в карточке объявления. */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isPhoneView ? "1fr" : "auto",
            gap: "10px",
            justifyContent: "flex-end",
            alignItems: "stretch",
            ...(isPhoneView ? {
              position: "sticky", bottom: 0, zIndex: 3,
              background: S.surface,
              margin: "0 -16px",
              padding: "10px 16px 12px",
              borderTop: "1px solid rgba(141,147,163,0.14)"
            } : {})
          }}>
            <button
              type="submit"
              disabled={loading}
              className="kl-plain-button kl-press-send"
              style={{
                ...accentButtonStyle,
                padding: "11px 20px",
                cursor: loading ? "not-allowed" : "pointer",
                borderRadius: "18px",
                fontSize: "14px",
                minWidth: isPhoneView ? "0" : "190px",
                width: "100%"
              }}
              onMouseEnter={(event) => {
                if (loading) return;
                applyHoverStyles(event, accentHoverEnter);
              }}
              onMouseLeave={(event) => {
                if (loading) return;
                applyHoverStyles(event, accentHoverLeave);
              }}
            >
              {loading ? (
                <>
                  <Spinner size={16} />
                  {uploadProgress.total > 1
                    ? tf`Добавление ${Math.min(uploadProgress.current, uploadProgress.total)}/${uploadProgress.total}...`
                    : accountAddMode === "registration"
                      ? t("Регистрация...")
                    : t("Добавление...")}
                </>
              ) : (
                <>
                  <span className="kl-btn-ico"><SendIcon size={16} /></span>
                  {accountAddMode === "registration"
                    ? !registrationSessionId
                      ? t("Начать регистрацию")
                      : registrationStage === "phoneCode"
                        ? t("Подтвердить SMS-код")
                      : registrationStage === "finalize"
                        ? t("Войти и добавить")
                        : t("Открыть email-ссылку")
                    : uploadMode === "file"
                      ? t("Добавить аккаунт(ы)")
                      : t("Добавить из текста")}
                </>
              )}
            </button>
          </div>
        </form>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .add-account-modal-root .kl-plain-button {
          transition: all 0.2s ease !important;
        }
        .add-account-modal-root .kl-plain-button:hover:not(:disabled) {
          background: linear-gradient(135deg, ${aa(700, 0.38)}, ${aa(600, 0.26)}) !important;
          border-color: ${aa(300, 0.56)} !important;
          box-shadow: 0 14px 28px ${aa(900, 0.24)} !important;
          transform: translateY(-2px) !important;
        }
        .add-account-modal-root .kl-plain-button:active:not(:disabled) {
          transform: translateY(0) !important;
        }
        html[data-theme="light"] .add-account-modal-root .kl-plain-button:hover:not(:disabled) {
          background: linear-gradient(135deg, ${A[100]}, ${A[50]}) !important;
          border-color: ${aa(500, 0.28)} !important;
          box-shadow: 0 8px 18px ${aa(500, 0.16)} !important;
          color: #0e1015 !important;
        }
      `}</style>
    </div>
  );
};

export default AddAccountModal;
